import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Dropdown, List, Avatar, Typography, Button, Space, Empty, theme } from 'antd';
import { BellOutlined, CheckOutlined, UserOutlined } from '@ant-design/icons';
import { notificationsApi } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pl';

dayjs.extend(relativeTime);
dayjs.locale('pl');

const { Text } = Typography;

interface Notification {
    id: string;
    type: string;
    title: string;
    message?: string;
    link?: string;
    isRead: boolean;
    createdAt: string;
    triggeredBy?: {
        id: string;
        firstName: string;
        lastName: string;
    };
}

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { token } = theme.useToken();

    const { data: countData } = useQuery({
        queryKey: ['notifications-count'],
        queryFn: () => notificationsApi.getUnreadCount().then((r: any) => r.data),
        refetchInterval: 30000, // Refresh every 30 seconds
    });

    const { data: notifications = [], isLoading } = useQuery<Notification[]>({
        queryKey: ['notifications'],
        queryFn: () => notificationsApi.getAll().then((r: any) => {
            console.log('[NotificationBell] Fetched notifications:', r.data);
            return r.data;
        }),
        enabled: open,
        staleTime: 0,
        refetchOnMount: true,
    });

    // Force refetch when opening the dropdown
    useEffect(() => {
        if (open) {
            console.log('[NotificationBell] Opening dropdown, forcing refetch');
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    }, [open, queryClient]);

    console.log('[NotificationBell] Notifications count:', notifications.length, 'IsLoading:', isLoading);

    const displayNotifications = notifications.slice(0, 5);

    const markAsReadMutation = useMutation({
        mutationFn: (id: string) => notificationsApi.markAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
        },
    });

    const markAllAsReadMutation = useMutation({
        mutationFn: () => notificationsApi.markAllAsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
        },
    });

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.isRead) {
            markAsReadMutation.mutate(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
        }
        setOpen(false);
    };

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'MENTION':
                return '💬';
            case 'ASSIGNMENT':
                return '📌';
            case 'UPDATE':
                return '📝';
            case 'COMMENT':
                return '💭';
            case 'REMINDER':
                return '⏰';
            default:
                return '🔔';
        }
    };

    const dropdownContent = (
        <div style={{
            width: 360,
            maxHeight: 400,
            background: token.colorBgElevated,
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadowSecondary,
            display: 'flex',
            flexDirection: 'column',
        }}>
            <div style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Text strong>Powiadomienia</Text>
                <Space>
                    <Button type="link" size="small" onClick={() => { setOpen(false); navigate('/notifications'); }}>
                        Zobacz wszystkie
                    </Button>
                    {notifications.some(n => !n.isRead) && (
                        <Button
                            type="link"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={() => markAllAsReadMutation.mutate()}
                        >
                            Oznacz wszystkie
                        </Button>
                    )}
                </Space>
            </div>

            <div style={{ overflow: 'auto', maxHeight: 300 }}>
                {displayNotifications.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Brak powiadomień"
                        style={{ padding: 24 }}
                    />
                ) : (
                    <List
                        loading={isLoading}
                        dataSource={displayNotifications}
                        renderItem={(item) => (
                            <List.Item
                                style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    background: item.isRead ? 'transparent' : token.colorSuccessBg,
                                    transition: 'background 0.3s',
                                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                }}
                                onClick={() => handleNotificationClick(item)}
                                className="notification-item"
                            >
                                <List.Item.Meta
                                    avatar={
                                        item.triggeredBy ? (
                                            <Avatar style={{ backgroundColor: '#1890ff' }}>
                                                {item.triggeredBy.firstName[0]}
                                            </Avatar>
                                        ) : (
                                            <Avatar icon={<UserOutlined />} />
                                        )
                                    }
                                    title={
                                        <Space>
                                            <span>{getNotificationIcon(item.type)}</span>
                                            <Text strong={!item.isRead}>{item.title}</Text>
                                        </Space>
                                    }
                                    description={
                                        <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                            {item.message && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {item.message.length > 60
                                                        ? item.message.slice(0, 60) + '...'
                                                        : item.message}
                                                </Text>
                                            )}
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {dayjs(item.createdAt).fromNow()}
                                            </Text>
                                        </Space>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                )}
            </div>

            {notifications.length > 5 && (
                <div style={{ padding: '8px 16px', borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
                    <Button type="link" size="small" onClick={() => { setOpen(false); navigate('/notifications'); }}>
                        Zobacz całą historię ({notifications.length})
                    </Button>
                </div>
            )}
        </div>
    );

    return (
        <Dropdown
            trigger={['click']}
            open={open}
            onOpenChange={setOpen}
            dropdownRender={() => dropdownContent}
            placement="bottomRight"
        >
            <Badge count={countData?.count || 0} size="small" offset={[-2, 2]}>
                <Button
                    type="text"
                    icon={<BellOutlined style={{ fontSize: 20 }} />}
                    style={{ padding: '4px 8px' }}
                />
            </Badge>
        </Dropdown>
    );
}
